import { useCallback, type MouseEvent } from 'react'
import { useReactFlow } from '@xyflow/react'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'
import { MaterialIcon } from '@/ui/material-icon'
import { useAppDispatch } from '@/app/reduxHooks'
import { openOverlay, type OverlayType } from '@/features/adp/state/overlaySlice'
import { setPanelOpen } from '@/features/adp/state/validationSlice'
import type { NodeValidationDecoration } from '../types/canvas.types'
import type { ValidationIssue } from '@/features/adp/utils/pipelineValidator'
import styles from './NodeValidationBadge.module.scss'

interface NodeValidationBadgeProps {
  nodeId: string
  validation: NodeValidationDecoration | undefined
}

export default function NodeValidationBadge({
  nodeId,
  validation,
}: Readonly<NodeValidationBadgeProps>) {
  const dispatch = useAppDispatch()
  const { setCenter, getNode } = useReactFlow()

  const handleFix = useCallback(
    (issue: ValidationIssue, e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      if (!issue.fix) return
      dispatch(
        openOverlay({
          type: issue.fix.overlayType as OverlayType,
          // Each overlay has its own typed payload; the fix builder sets the
          // matching shape per rule.
          data: issue.fix.payload as never,
        })
      )
    },
    [dispatch]
  )

  const handleLocate = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      const node = getNode(nodeId)
      if (!node) return
      const x = node.position.x + (node.measured?.width ?? 240) / 2
      const y = node.position.y + (node.measured?.height ?? 120) / 2
      setCenter(x, y, { zoom: 1.2, duration: 400 })
    },
    [getNode, nodeId, setCenter]
  )

  const handleViewAll = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      dispatch(setPanelOpen(true))
    },
    [dispatch]
  )

  if (!validation) return null

  const tone = validation.severity === 'error' ? 'error' : 'warning'

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`${styles.badge} ${styles[tone]} nodrag`}
          aria-label={`${validation.count} validation ${
            validation.count === 1 ? 'issue' : 'issues'
          }`}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        >
          <MaterialIcon name={tone === 'error' ? 'error' : 'warning'} size={14} />
          <span className={styles.count}>{validation.count}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        className={styles.popover}
        onPointerDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        <div className={styles.header}>
          <MaterialIcon
            name={tone === 'error' ? 'error' : 'warning'}
            size={16}
            className={styles[`headerIcon_${tone}`]}
          />
          <span className={styles.headerTitle}>
            {validation.count} {validation.count === 1 ? 'issue' : 'issues'} on this node
          </span>
        </div>
        <ul className={styles.list}>
          {validation.issues.map(issue => (
            <li key={issue.id} className={styles.item}>
              <div className={styles.itemTitle}>{issue.title}</div>
              <div className={styles.itemMessage}>{issue.message}</div>
              {issue.fix && (
                <button
                  type="button"
                  className={styles.fixButton}
                  onClick={e => handleFix(issue, e)}
                >
                  <MaterialIcon name="build" size={14} />
                  Fix
                </button>
              )}
            </li>
          ))}
        </ul>
        <div className={styles.footer}>
          <button type="button" className={styles.footerButton} onClick={handleLocate}>
            <MaterialIcon name="my_location" size={14} />
            Locate
          </button>
          <button type="button" className={styles.footerButton} onClick={handleViewAll}>
            <MaterialIcon name="list" size={14} />
            View all
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
